import { useMemo, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "./use-auth";
import { MsalService } from "@/components/security/msal-service";
import type { GetUserProfileResponse } from "@/domain/core";
import { config } from "@/config/environment";
import { useAuthenticatedUsersApi } from "@/api";
import type { UserDetails } from "@/types/core";
import { USER_QUERY_OPTIONS } from "@/api/abstractions/api-config";
import { getApiErrorDetail } from "@/utilities/api-utils";

// ============================================================================
// USER DETAILS HOOK
// ============================================================================

export function useUserDetails(): UserDetails {
  const { userInfo, isAuthenticated } = useAuth();
  const { instance } = useMsal();
  const msalService = MsalService.getInstance(instance);

  // Extract userId from token claims
  const userId = useMemo(() => {
    if (!userInfo || !isAuthenticated) return undefined;

    // Safeguard: Ensure MSAL instance is available and initialized
    if (!instance) {
      if (config.debug.enabled) {
        console.warn("MSAL instance not available");
      }
      return undefined;
    }

    // Get the current account from MSAL
    const account = msalService.getActiveAccount();

    // If no active account is available, return undefined
    if (!account) {
      if (config.debug.enabled) {
        console.warn("No active account available");
      }
      return undefined;
    }

    // Return the local account ID
    return account.localAccountId;
  }, [userInfo, isAuthenticated, instance, msalService]);

  // Fetch user details from backend API
  const { useGetUserProfileQuery } = useAuthenticatedUsersApi();
  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch
  } = useGetUserProfileQuery(
    { userId: userId! },
    {
      skip: !userId || !isAuthenticated,
      ...USER_QUERY_OPTIONS
    }
  );

  // Enhanced error handling for different API error scenarios
  const apiError = getApiErrorDetail(error) ?? undefined;
  const isUserInactive =
    isError &&
    apiError?.status === 403 &&
    apiError?.code === "User.Inactive";

  // Check for registration required from successful API response (200 OK)
  const isRegistrationRequired =
    !isError && user && user.registrationRequired === true ? true : false;

  const isServerError =
    isError &&
    apiError?.status &&
    apiError.status >= 500 &&
    apiError.status < 600
      ? true
      : false;

  // Computed properties
  // Only set userType if user is fully registered (not requiring registration)
  const userType =
    user && !user.registrationRequired ? user.userType : undefined;
  const isProfessional = userType === "professional";
  const isPractice = userType === "practice";
  const isAdmin = userType === "admin";
  const isSupport = userType === "support";
  // Internal staff. Governs access to the admin console; sensitive actions inside
  // it check isAdmin, mirroring HasStaffRole vs HasAdminRole on the API.
  const isStaff = isAdmin || isSupport;

  // Debug logging — metadata only, never PII/tokens/profile payloads
  useEffect(() => {
    if (!(import.meta.env.DEV && config.debug.enabled)) {
      return;
    }

    console.log("[useUserDetails]", {
      hasUserId: Boolean(userId),
      registrationRequired: Boolean(user?.registrationRequired),
      isLoading,
      hasError: Boolean(error) || Boolean(apiError)
    });
  }, [userId, user?.registrationRequired, isLoading, error, apiError]);

  return {
    // Basic user info from token
    name: userInfo?.name || "",
    email: userInfo?.email || "",

    // Full user details from backend
    user,

    // Loading states
    isLoading,
    isError,
    error,
    apiError,

    // Specific error scenarios
    isUserInactive,
    isRegistrationRequired,
    isServerError,

    // Registration details
    registrationMessage: user?.registrationMessage || "",

    // Computed properties
    userId: userId || undefined,
    userType,
    isProfessional,
    isPractice,
    isAdmin,
    isSupport,
    isStaff,

    // Refetch method for manual refresh
    refetch
  };
}

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

/**
 * Hook to check if current user is a professional
 */
export function useIsProfessional(): boolean {
  const { isProfessional } = useUserDetails();
  return isProfessional;
}

/**
 * Hook to check if current user is a practice
 */
export function useIsPractice(): boolean {
  const { isPractice } = useUserDetails();
  return isPractice;
}

/**
 * Hook to check if current user is an admin
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = useUserDetails();
  return isAdmin;
}

/**
 * Hook to check if current user is a support user
 */
export function useIsSupport(): boolean {
  const { isSupport } = useUserDetails();
  return isSupport;
}

/**
 * Hook to check if current user is internal staff (admin or support)
 */
export function useIsStaff(): boolean {
  const { isStaff } = useUserDetails();
  return isStaff;
}

/**
 * Hook to get the current user's ID
 */
export function useUserId(): string | undefined {
  const { userId } = useUserDetails();
  return userId;
}

/**
 * Hook to get the current user's full details
 */
export function useCurrentUser(): GetUserProfileResponse | undefined {
  const { user } = useUserDetails();
  return user;
}
